import { FC } from 'react';
import { useProfile } from '@/hooks/useProfile';
import { AffiliateEligibility } from '@/components/affiliates/AffiliateEligibility';
import { Handshake, DollarSign, Users, Star, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Navigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

const Affiliates: FC = () => {
  const { userProfile, isLoading, error } = useProfile();

  if (error) {
    console.error('Error loading profile:', error);
    return <Navigate to="/login" />;
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-8">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-24 w-full" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-8 space-y-8">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-8"
        >
          <Handshake className="w-8 h-8 text-primary animate-pulse" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Programa de Socios
          </h1>
        </motion.div>

        <div className="grid gap-8 mb-12">
          <section className="prose max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed">
              Únete a nuestro programa de socios y forma parte de nuestra misión de educar financieramente 
              a la próxima generación. Como socio, podrás recomendar nuestra plataforma y ganar comisiones 
              por cada nueva suscripción.
            </p>
          </section>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="p-6 bg-white/80 backdrop-blur-sm hover:shadow-lg transition-shadow">
              <DollarSign className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Comisiones Atractivas</h3>
              <p className="text-gray-600">
                Gana un 50% de comisión por cada nueva suscripción que generes. Las comisiones se pagan 
                mensualmente a través de PayPal.
              </p>
            </Card>

            <Card className="p-6 bg-white/80 backdrop-blur-sm hover:shadow-lg transition-shadow">
              <Users className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Impacto Social</h3>
              <p className="text-gray-600">
                Ayuda a más familias a acceder a educación financiera de calidad mientras construyes 
                un ingreso pasivo.
              </p>
            </Card>

            <Card className="p-6 bg-white/80 backdrop-blur-sm hover:shadow-lg transition-shadow">
              <Award className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Beneficios Exclusivos</h3>
              <p className="text-gray-600">
                Accede a materiales promocionales, soporte dedicado y recursos exclusivos para 
                maximizar tus ganancias.
              </p>
            </Card>
          </div>

          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <Award className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-semibold text-gray-800">¿Por qué unirte?</h2>
            </div>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Sin inversión inicial requerida</li>
              <li>Comisiones recurrentes por suscripciones activas</li>
              <li>Materiales promocionales listos para usar</li>
              <li>Panel de control para seguimiento de comisiones</li>
              <li>Soporte personalizado para socios</li>
            </ul>
          </div>
        </div>

        <AffiliateEligibility userProfile={userProfile} />
      </div>
    </div>
  );
};

export default Affiliates;